import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { authApi } from '@/api/auth'
import { useAuthStore } from '@/store/auth'
import { PasswordInput } from '@/components/common/PasswordInput'

const loginSchema = z.object({
  email: z.string().email('Введите корректный email'),
  password: z.string().min(1, 'Введите пароль'),
})
const forgotSchema = z.object({
  email: z.string().email('Введите корректный email'),
})

type LoginForm = z.infer<typeof loginSchema>
type ForgotForm = z.infer<typeof forgotSchema>
type Mode = 'login' | 'forgot' | 'sent'

const inputCls = 'block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500'

export default function LoginPage() {
  const navigate = useNavigate()
  const { setToken, setUser } = useAuthStore()
  const [serverError, setServerError] = useState('')
  const [mode, setMode] = useState<Mode>('login')

  const loginForm = useForm<LoginForm>({ resolver: zodResolver(loginSchema) })
  const forgotForm = useForm<ForgotForm>({ resolver: zodResolver(forgotSchema) })

  async function onLogin(data: LoginForm) {
    setServerError('')
    try {
      const { data: tokenData } = await authApi.login(data.email, data.password)
      setToken(tokenData.access_token)
      const { data: me } = await authApi.me()
      setUser(me)
      navigate(me.role === 'director' ? '/dashboard' : '/projects')
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 401) setServerError('Неверный email или пароль')
      else if (status === 423) setServerError('Аккаунт временно заблокирован')
      else setServerError('Ошибка сервера. Попробуйте позже.')
    }
  }

  async function onForgot(data: ForgotForm) {
    setServerError('')
    try {
      await authApi.forgotPassword(data.email)
    } catch {
      // намеренно игнорируем ошибки — не раскрываем наличие аккаунта
    }
    setMode('sent')
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="text-2xl font-bold text-brand-600">СУП ГГП</div>
          <div className="mt-1 text-sm text-gray-500">Система управления проектами</div>
        </div>

        <div className="rounded-xl bg-white p-8 shadow-sm border border-gray-200">

          {/* ── Режим: Вход ── */}
          {mode === 'login' && (
            <>
              <h1 className="mb-6 text-lg font-semibold text-gray-900">Вход в систему</h1>
              <form onSubmit={loginForm.handleSubmit(onLogin)} noValidate className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input id="email" type="email" autoComplete="email" {...loginForm.register('email')} className={inputCls} placeholder="you@example.com" />
                  {loginForm.formState.errors.email && (
                    <p className="mt-1 text-xs text-rose-600">{loginForm.formState.errors.email.message}</p>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">Пароль</label>
                    <button type="button" onClick={() => { setServerError(''); setMode('forgot') }}
                      className="text-xs text-brand-600 hover:text-brand-700">
                      Забыли пароль?
                    </button>
                  </div>
                  <PasswordInput id="password" autoComplete="current-password" {...loginForm.register('password')} className={inputCls} />
                  {loginForm.formState.errors.password && (
                    <p className="mt-1 text-xs text-rose-600">{loginForm.formState.errors.password.message}</p>
                  )}
                </div>
                {serverError && (
                  <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">{serverError}</div>
                )}
                <button type="submit" disabled={loginForm.formState.isSubmitting}
                  className="mt-2 w-full rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 transition-colors">
                  {loginForm.formState.isSubmitting ? 'Вход...' : 'Войти'}
                </button>
              </form>
            </>
          )}

          {/* ── Режим: Восстановление пароля ── */}
          {mode === 'forgot' && (
            <>
              <h1 className="mb-2 text-lg font-semibold text-gray-900">Восстановление пароля</h1>
              <p className="mb-6 text-sm text-gray-500">Введите email - пришлём ссылку для установки нового пароля.</p>
              <form onSubmit={forgotForm.handleSubmit(onForgot)} noValidate className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" autoComplete="email" {...forgotForm.register('email')} className={inputCls} placeholder="you@example.com" />
                  {forgotForm.formState.errors.email && (
                    <p className="mt-1 text-xs text-rose-600">{forgotForm.formState.errors.email.message}</p>
                  )}
                </div>
                <button type="submit" disabled={forgotForm.formState.isSubmitting}
                  className="w-full rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors">
                  {forgotForm.formState.isSubmitting ? 'Отправляем...' : 'Отправить ссылку'}
                </button>
                <button type="button" onClick={() => setMode('login')}
                  className="w-full text-sm text-gray-500 hover:text-gray-700">
                  ← Вернуться ко входу
                </button>
              </form>
            </>
          )}

          {/* ── Режим: Письмо отправлено ── */}
          {mode === 'sent' && (
            <div className="text-center space-y-4">
              <div className="text-emerald-500 text-5xl">✉</div>
              <h1 className="text-lg font-semibold text-gray-900">Письмо отправлено</h1>
              <p className="text-sm text-gray-500">
                Если этот email зарегистрирован в системе - вы получите письмо со ссылкой для сброса пароля.
                Ссылка действует 1 час.
              </p>
              <button onClick={() => setMode('login')}
                className="w-full rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 transition-colors">
                Вернуться ко входу
              </button>
            </div>
          )}

        </div>

        <p className="mt-4 text-center text-xs text-gray-400">ГрандГеоПроект © 2026</p>
      </div>
    </div>
  )
}
